import React, { useState, useEffect } from 'react';
import { getAcpLogs } from '@api/resident';
import { ApiResponse, ListApiResponse } from '@typings/ApiResponse';
import { AcpUpdateData } from '@typings/ACPTypes';
import Pagination from '@components/pagination';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

interface AcpUpdatedTableProps {
  partnerCarehomeId: string;
}

const AcpUpdatedTable: React.FC<AcpUpdatedTableProps> = ({
  partnerCarehomeId,
}) => {
  const [acpLogList, setAcpLogList] = useState<
    {
      patientName: string;
      patientSlug: string;
      timeAndDate: string;
      professionalName: string;
    }[]
  >([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetAcpLogs = async () => {
      if (partnerCarehomeId) {
        try {
          const res: ApiResponse<ListApiResponse<AcpUpdateData>> =
            await getAcpLogs(partnerCarehomeId, currentPage, 5);
          if (res?.status === 'success') {
            const transformData = res?.data?.data?.map(item => {
              return {
                patientName: item.patient.profile.full_name,
                patientSlug: item.patient.slug,
                timeAndDate: item.updatedAt,
                professionalName: item.full_name,
              };
            });

            setAcpLogList(transformData);
            setTotalPages(res?.data?.pages);
          }
        } catch (error) {
          console.error(error);
        }
      }
    };
    fetAcpLogs();
  }, [currentPage, partnerCarehomeId]);

  const redirectToLogDetails = (e: any, slug: string) => {
    e.preventDefault();
    navigate(`/patient/${slug}`, {
      state: { currentStep: 3, scrollTo: 'dateLastUpdated' },
    });
  };

  return (
    <div className="mt-5">
      <h1 className="govuk-heading-l">Latest Updates</h1>
      <p className="govuk-body">
        The table below displays when an Advance Care Plan was created or last
        updated. This data excludes when an Advance Care Plan is shared,
        conversations are logged and Patient Notes are documented.{' '}
      </p>
      <table className="govuk-table govuk-table--small-text-until-tablet">
        <thead className="govuk-table__head">
          <tr className="govuk-table__row">
            <th scope="col" className="govuk-table__header">
              Patient name
            </th>
            <th scope="col" className="govuk-table__header">
              Time and date
            </th>
            <th scope="col" className="govuk-table__header">
              Name of professional
            </th>
          </tr>
        </thead>
        <tbody className="govuk-table__body">
          {acpLogList?.map(item => {
            return (
              <tr className="govuk-table__row" key={item.timeAndDate}>
                <th scope="row" className="govuk-table__header">
                  <a
                    href={`/patient/${item.patientSlug}/notes`}
                    className="govuk-link govuk-!-font-weight-bold govuk-link--no-visited-state"
                  >
                    {item.patientName}
                  </a>
                </th>
                <td className="govuk-table__cell">
                  <div onClick={e => redirectToLogDetails(e, item.patientSlug)}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <a
                      href=""
                      className="govuk-link govuk-link--no-visited-state"
                    >
                      {dayjs(new Date(item.timeAndDate)).format(
                        'h:mm:ss A, D MMMM, YYYY'
                      )}
                    </a>
                  </div>
                </td>
                <td className="govuk-table__cell">{item.professionalName}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </div>
  );
};

export default AcpUpdatedTable;
